// Reine Logik des Reiters „Dokumente“ (/p/<projekt>/dokumente): Dateityp, Ablageorte im Klartext, Filter und Sortierung,
// Filter in der Adresse (?q=…&ort=schritt&typ=pdf&verwaist=1&sort=name). Ohne React, damit Unit-Tests sie direkt prüfen.
import type { Anhang, AnhangBezugTyp } from './api';

// ---------- Dateityp ----------

export type DateiArt = 'pdf' | 'word' | 'excel' | 'bild' | 'sonstige';
export const DATEI_ARTEN: { value: DateiArt; label: string }[] = [
  { value: 'pdf', label: 'PDF' }, { value: 'word', label: 'Word' }, { value: 'excel', label: 'Excel' },
  { value: 'bild', label: 'Bild' }, { value: 'sonstige', label: 'Sonstige' },
];
const ARTEN: Record<string, DateiArt> = {
  pdf: 'pdf',
  doc: 'word', docx: 'word', odt: 'word', rtf: 'word',
  xls: 'excel', xlsx: 'excel', ods: 'excel', csv: 'excel',
  png: 'bild', jpg: 'bild', jpeg: 'bild', gif: 'bild', webp: 'bild', bmp: 'bild', tif: 'bild', tiff: 'bild', heic: 'bild',
};

/** Endung des Dateinamens in Kleinbuchstaben ('' ohne Endung). */
export const endung = (name: string) => { const i = name.lastIndexOf('.'); return i < 0 ? '' : name.slice(i + 1).toLowerCase(); };

/** Dateityp für Filter und Symbol (nach der Endung). */
export const dateiArt = (name: string): DateiArt => ARTEN[endung(name)] ?? 'sonstige';

/**
 * Kann der Browser die Datei selbst anzeigen („Öffnen“ in neuem Tab)? Wie der Server (Anhaenge.ImBrowser) –
 * HTML, SVG und XML nie, Office-Dateien und Archive werden heruntergeladen.
 */
const IM_BROWSER = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'txt', 'csv', 'md', 'json', 'mp4', 'mp3', 'm4a', 'wav']);
export const imBrowserAnzeigbar = (name: string) => IM_BROWSER.has(endung(name));

// ---------- Ablageorte ----------

export interface Ablageort {
  bezugTyp: AnhangBezugTyp;
  bezugId: string;
  /** Element gibt es nicht mehr */
  verwaist: boolean;
  /** nur verknüpft (nicht der Ort des Hochladens) */
  verknuepft: boolean;
  /** bei Verknüpfungen: wer verknüpft hat (für „Verknüpfung lösen“) */
  verknuepftVon?: string;
}

/** Alle Orte einer Datei: zuerst der Ort des Hochladens, dann die Verknüpfungen. */
export function ablageorte(a: Anhang): Ablageort[] {
  return [
    { bezugTyp: a.bezugTyp, bezugId: a.bezugId, verwaist: !!a.verwaist, verknuepft: false },
    ...(a.verknuepfungen ?? []).map((v) => ({ bezugTyp: v.bezugTyp, bezugId: v.bezugId, verwaist: !!v.verwaist, verknuepft: true, verknuepftVon: v.verknuepftVon })),
  ];
}

export const ORT_NAME: Record<AnhangBezugTyp, string> = { projekt: 'Projekt', prozess: 'Prozess', schritt: 'Schritt', entscheidung: 'Entscheidung', aufgabe: 'Aufgabe' };

/** Titel eines Elements nachschlagen (undefined = unbekannt). */
export type TitelVon = (typ: AnhangBezugTyp, id: string) => string | undefined;

/** Ort im Klartext: „Projekt“, „Schritt „Ware prüfen““, „Aufgabe (gelöscht)“. */
export function ortText(o: Pick<Ablageort, 'bezugTyp' | 'bezugId' | 'verwaist'>, titel: TitelVon): string {
  if (o.bezugTyp === 'projekt') return 'Projekt';
  if (o.verwaist) return ORT_NAME[o.bezugTyp] + ' (gelöscht)';
  const t = titel(o.bezugTyp, o.bezugId);
  return t ? `${ORT_NAME[o.bezugTyp]} „${t}“` : ORT_NAME[o.bezugTyp];
}

// ---------- Filter und Sortierung ----------

export type SortFeld = 'name' | 'datum' | 'groesse';
export interface DokFilter {
  /** Freitext: jedes Wort im Dateinamen oder in einem Ablageort */
  text: string;
  /** '' = alle Orte, sonst Art des Ablageorts (hochgeladen oder verknüpft) */
  ort: '' | AnhangBezugTyp;
  /** '' = alle Typen */
  art: '' | DateiArt;
  /** nur Dateien, deren Element gelöscht ist */
  verwaist: boolean;
  sort: SortFeld;
  absteigend: boolean;
}
/** Standard: neueste zuerst. */
export const DOK_FILTER_LEER: DokFilter = { text: '', ort: '', art: '', verwaist: false, sort: 'datum', absteigend: true };

const ORTE: AnhangBezugTyp[] = ['projekt', 'prozess', 'schritt', 'entscheidung', 'aufgabe'];
const STANDARD_RICHTUNG: Record<SortFeld, boolean> = { name: false, datum: true, groesse: true };

/** Filter aus der Adresse: ?q=…&ort=schritt&typ=pdf&verwaist=1&sort=name|datum|groesse&richtung=auf|ab. */
export function dokFilterAusSuche(suche: string): DokFilter {
  const q = new URLSearchParams(suche);
  const ort = q.get('ort') ?? '', art = q.get('typ') ?? '', sort = q.get('sort') ?? '', richtung = q.get('richtung');
  const feld: SortFeld = sort === 'name' || sort === 'groesse' || sort === 'datum' ? sort : 'datum';
  return {
    text: q.get('q') ?? '',
    ort: (ORTE as string[]).includes(ort) ? (ort as AnhangBezugTyp) : '',
    art: DATEI_ARTEN.some((x) => x.value === art) ? (art as DateiArt) : '',
    verwaist: q.get('verwaist') === '1',
    sort: feld,
    absteigend: richtung === 'ab' ? true : richtung === 'auf' ? false : STANDARD_RICHTUNG[feld],
  };
}

/** Filter als Abfrageteil der Adresse (ohne „?“); Standardwerte fallen weg. */
export function dokFilterSuche(f: DokFilter): string {
  const q = new URLSearchParams();
  if (f.text.trim()) q.set('q', f.text);
  if (f.ort) q.set('ort', f.ort);
  if (f.art) q.set('typ', f.art);
  if (f.verwaist) q.set('verwaist', '1');
  if (f.sort !== 'datum') q.set('sort', f.sort);
  if (f.absteigend !== STANDARD_RICHTUNG[f.sort]) q.set('richtung', f.absteigend ? 'ab' : 'auf');
  return q.toString();
}

/** Sind Such- oder Filterkriterien gesetzt (die Sortierung zählt nicht)? */
export const dokFilterAktiv = (f: DokFilter) => !!(f.text.trim() || f.ort || f.art || f.verwaist);

/** Klick auf einen Spaltenkopf: gleiche Spalte kehrt die Richtung um, neue Spalte beginnt mit ihrer Standardrichtung. */
export const sortierenNach = (f: DokFilter, feld: SortFeld): DokFilter =>
  f.sort === feld ? { ...f, absteigend: !f.absteigend } : { ...f, sort: feld, absteigend: STANDARD_RICHTUNG[feld] };

/** Passt die Datei zum Filter? */
export function dokPasst(a: Anhang, f: DokFilter, titel: TitelVon): boolean {
  const orte = ablageorte(a);
  if (f.ort && !orte.some((o) => o.bezugTyp === f.ort)) return false;
  if (f.art && dateiArt(a.dateiname) !== f.art) return false;
  if (f.verwaist && !a.verwaist) return false;
  const woerter = f.text.toLowerCase().split(/\s+/).filter(Boolean);
  if (woerter.length) {
    const heu = [a.dateiname, ...orte.map((o) => ortText(o, titel))].join('\n').toLowerCase();
    if (!woerter.every((w) => heu.includes(w))) return false;
  }
  return true;
}

/** Sortiert (neue Liste); bei Gleichstand nach Name, dann Kennung – damit die Reihenfolge stabil bleibt. */
export function dokSortieren(liste: Anhang[], f: Pick<DokFilter, 'sort' | 'absteigend'>): Anhang[] {
  const name = (x: Anhang, y: Anhang) => x.dateiname.localeCompare(y.dateiname, 'de', { sensitivity: 'base', numeric: true });
  const vergleich = f.sort === 'name' ? name
    : f.sort === 'groesse' ? (x: Anhang, y: Anhang) => x.groesse - y.groesse
    : (x: Anhang, y: Anhang) => x.hochgeladenAm.localeCompare(y.hochgeladenAm);
  const r = f.absteigend ? -1 : 1;
  return [...liste].sort((x, y) => r * vergleich(x, y) || name(x, y) || x.id.localeCompare(y.id));
}

/** Gefilterte und sortierte Dateien. */
export const dokumenteZeigen = (liste: Anhang[], f: DokFilter, titel: TitelVon) => dokSortieren(liste.filter((a) => dokPasst(a, f, titel)), f);

/** Wert eines Ablageorts für die Auswahl („schritt:abc“) und zurück. */
export const ortWert = (typ: AnhangBezugTyp, id: string) => `${typ}:${id}`;
export function ortAusWert(v: string): { bezugTyp: AnhangBezugTyp; bezugId: string } | null {
  const i = v.indexOf(':');
  const typ = v.slice(0, i) as AnhangBezugTyp;
  return i > 0 && ORTE.includes(typ) && v.length > i + 1 ? { bezugTyp: typ, bezugId: v.slice(i + 1) } : null;
}
